import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery } from "@apollo/client";
import { LayoutGrid, LayoutList } from "lucide-react";
import { useState } from "react";

const STREAM_SCHEDULES_QUERY = gql`
    query StreamSchedules {
        streamSchedules {
            id
            streamTeamMember {
                id
                player {
                    id
                    name
                    avatarUrl
                }
                twitchUsername
                twitchAuth {
                    profileImageUrl
                }
            }
            title
            category
            startTime
            endTime
            isRecurring
        }
    }
`;

interface StreamSchedule {
    id: string;
    streamTeamMember: {
        id: string;
        player: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
        twitchUsername: string;
        twitchAuth?: {
            profileImageUrl: string;
        } | null;
    };
    title: string;
    category: string;
    startTime: string;
    endTime: string | null;
    isRecurring: boolean;
}

function dateKeyToLocalDate(dateKey: string): Date {
    const [yearStr, monthStr, dayStr] = dateKey.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    // Avoid `new Date("YYYY-MM-DD")` because it is parsed as UTC.
    return new Date(year, month - 1, day);
}

export default function StreamTeamSchedulePage() {
    const [viewMode, setViewMode] = useState<"detailed" | "compact">("compact");

    const { loading, error, data } = useQuery<{
        streamSchedules: StreamSchedule[];
    }>(STREAM_SCHEDULES_QUERY);

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-zinc-400">
                    Loading schedule...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-red-400">
                    Error loading schedule: {error.message}
                </p>
            </div>
        );
    }

    const schedules = data?.streamSchedules || [];

    // Filter to only show upcoming schedules
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Start from beginning of today

    const upcomingSchedules = schedules.filter(
        (schedule) => new Date(schedule.startTime) >= now,
    );

    // Group schedules by LOCAL date (not UTC)
    const groupedSchedules = upcomingSchedules.reduce(
        (acc, schedule) => {
            const date = new Date(schedule.startTime);
            // Use local date components instead of ISO string
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            const dateKey = `${year}-${month}-${day}`;
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(schedule);
            return acc;
        },
        {} as Record<string, StreamSchedule[]>,
    );

    // Generate all dates for the next 30 days, starting from today (in local timezone)
    const allDates: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 30; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const dateKey = `${year}-${month}-${day}`;
        allDates.push(dateKey);
    }
    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            <div className="mb-6 sm:mb-8">
                <h1 className="font-prviking text-brand mb-3 text-center text-3xl sm:mb-4 sm:text-4xl lg:text-5xl">
                    {convertLetters("Schedule")}
                </h1>
                <p className="font-garamond mb-4 text-center text-sm text-zinc-400 sm:mb-6 sm:text-base">
                    Your 30-day overview of scheduled VRX streams and events.
                </p>

                {/* View Toggle */}
                <div className="mb-4 flex justify-center">
                    <div className="inline-flex rounded-lg border-2 border-zinc-700 bg-zinc-800 p-1">
                        <button
                            onClick={() => setViewMode("detailed")}
                            className={`font-garamond flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all sm:px-4 sm:text-base ${
                                viewMode === "detailed"
                                    ? "bg-brand text-zinc-900"
                                    : "text-zinc-300 hover:text-white"
                            }`}
                        >
                            <LayoutList className="h-4 w-4" />
                            <span className="hidden sm:inline">Detailed</span>
                        </button>
                        <button
                            onClick={() => setViewMode("compact")}
                            className={`font-garamond flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all sm:px-4 sm:text-base ${
                                viewMode === "compact"
                                    ? "bg-brand text-zinc-900"
                                    : "text-zinc-300 hover:text-white"
                            }`}
                        >
                            <LayoutGrid className="h-4 w-4" />
                            <span className="hidden sm:inline">Compact</span>
                        </button>
                    </div>
                </div>
                <p className="text-center text-zinc-400 italic">
                    All times are displayed in your local time zone.
                </p>
            </div>

            <div
                className={
                    viewMode === "detailed"
                        ? "grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                        : "grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
                }
            >
                {allDates.map((dateKey) => {
                    const date = dateKeyToLocalDate(dateKey);
                    const daySchedules = (groupedSchedules[dateKey] || []).sort(
                        (a, b) =>
                            new Date(a.startTime).getTime() -
                            new Date(b.startTime).getTime(),
                    );

                    return viewMode === "detailed" ? (
                        <DateCard
                            key={dateKey}
                            date={date}
                            schedules={daySchedules}
                        />
                    ) : (
                        <CompactDateCard
                            key={dateKey}
                            date={date}
                            schedules={daySchedules}
                        />
                    );
                })}
            </div>
        </div>
    );
}

interface DateCardProps {
    date: Date;
    schedules: StreamSchedule[];
}

function DateCard({ date, schedules }: DateCardProps) {
    const dayNumber = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });

    return (
        <div className="border-brand_stream_team overflow-hidden rounded-xl border-2 bg-zinc-900 shadow-xl transition-all hover:border-yellow-500">
            {/* Date Header */}
            <div className="bg-brand p-3 text-center">
                <div className="font-prviking text-4xl font-bold text-zinc-900">
                    {dayNumber}
                </div>
                <div className="font-garamond text-sm font-medium text-zinc-900">
                    {month}
                </div>
                <div className="font-garamond text-xs text-zinc-800">
                    {dayName}
                </div>
            </div>

            {/* Streams */}
            <div className="space-y-2 p-3">
                {schedules.map((schedule) => {
                    const startTime = new Date(schedule.startTime);
                    const timeStr = startTime.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    });

                    return (
                        <div
                            key={schedule.id}
                            className="group cursor-pointer rounded-lg bg-zinc-900 p-2 transition-all hover:bg-zinc-700"
                        >
                            {/* Avatar and Name */}
                            <div className="mb-2 flex items-center gap-2">
                                {(() => {
                                    const avatarUrl =
                                        schedule.streamTeamMember.twitchAuth
                                            ?.profileImageUrl ||
                                        schedule.streamTeamMember.player
                                            .avatarUrl;
                                    return avatarUrl ? (
                                        <img
                                            src={avatarUrl}
                                            alt={
                                                schedule.streamTeamMember.player
                                                    .name
                                            }
                                            className="h-8 w-8 rounded-full border border-yellow-500 object-cover"
                                        />
                                    ) : (
                                        <div className="bg-brand flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-zinc-900">
                                            {schedule.streamTeamMember.player.name
                                                .charAt(0)
                                                .toUpperCase()}
                                        </div>
                                    );
                                })()}
                                <div className="flex-1 overflow-hidden">
                                    <div className="font-garamond truncate text-xs font-bold text-zinc-200">
                                        {schedule.streamTeamMember.player.name}
                                    </div>
                                    <div className="font-garamond text-xs text-zinc-400">
                                        {timeStr}
                                    </div>
                                </div>
                            </div>

                            {/* Stream Title */}
                            <div className="font-garamond mb-1 line-clamp-2 text-xs text-zinc-300">
                                {schedule.title || "Untitled Stream"}
                            </div>

                            {/* Category */}
                            {schedule.category && (
                                <div className="font-garamond truncate text-xs text-zinc-500">
                                    {schedule.category}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

interface CompactDateCardProps {
    date: Date;
    schedules: StreamSchedule[];
}

function CompactDateCard({ date, schedules }: CompactDateCardProps) {
    const dayNumber = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });

    return (
        <div className="border-brand_stream_team overflow-hidden rounded-lg border-2 bg-zinc-900 shadow-xl transition-all hover:border-yellow-500">
            {/* Date Header */}
            <div className="bg-brand p-2 text-center">
                <div className="font-prviking text-2xl font-bold text-zinc-900 sm:text-3xl">
                    {dayNumber}
                </div>
                <div className="font-garamond text-xs font-medium text-zinc-900">
                    {month}
                </div>
            </div>

            {/* Compact Streams */}
            <div className="flex flex-col gap-1 p-2">
                {schedules.map((schedule) => {
                    const startTime = new Date(schedule.startTime);
                    const timeStr = startTime.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                    });

                    const avatarUrl =
                        schedule.streamTeamMember.twitchAuth?.profileImageUrl ||
                        schedule.streamTeamMember.player.avatarUrl;

                    return (
                        <div
                            key={schedule.id}
                            className="group cursor-pointer rounded bg-zinc-900 p-1.5 transition-all hover:bg-zinc-700"
                            title={`${schedule.streamTeamMember.player.name}: ${schedule.title || "Untitled"}`}
                        >
                            <div className="flex items-center gap-1.5">
                                {avatarUrl ? (
                                    <img
                                        src={avatarUrl}
                                        alt={
                                            schedule.streamTeamMember.player
                                                .name
                                        }
                                        className="h-6 w-6 rounded-full border border-yellow-500 object-cover"
                                    />
                                ) : (
                                    <div className="bg-brand flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-zinc-900">
                                        {schedule.streamTeamMember.player.name
                                            .charAt(0)
                                            .toUpperCase()}
                                    </div>
                                )}
                                <div className="font-garamond min-w-0 flex-1 text-xs text-zinc-400">
                                    {timeStr}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {schedules.length === 0 && (
                    <div className="font-garamond py-2 text-center text-xs text-zinc-500">
                        No streams
                    </div>
                )}
            </div>
        </div>
    );
}
